import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Layers3,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import type { BookingRecord, TimeLog, VersionMirrorRecord } from "../models/production";
import type { Asset, AssetRateCard, Person, RateCard, Shot } from "../types";
import { isBidItemIncluded } from "../lib/bidStatus";
import { getDateRangeDays, isWeekend } from "../services/planningBlocks";
import { cn, formatCurrency } from "../utils";

interface ProjectUtilisationProps {
  projectId: string;
  fptProjectId?: string | null;
  projectName?: string;
  projectStartDate?: string;
  projectEndDate?: string;
  shots?: Shot[];
  assets?: Asset[];
  rates?: RateCard;
  assetRates?: AssetRateCard;
  supervisionPercentage?: number;
  currency?: string;
}

type DateDisplayFormat = "dd-mm-yyyy" | "dd/mm/yyyy" | "yyyy-mm-dd";

type GroupMode = "department" | "flat";

type ArtistRow = {
  id: string;
  name: string;
  department: string;
  roleTitle?: string;
  photoUrl?: string;
  fptPersonId?: string;
  fptUserId?: string;
  bookings: BookingRecord[];
  versionDays: string[];
  versionDaySet: Set<string>;
  versionCount: number;
  unbookedVersionDays: string[];
  versionsByDay: Map<string, VersionMirrorRecord[]>;
  allVersionsByDay: Map<string, VersionMirrorRecord[]>;
  bookedDays: number;
  bookedWorkingDays: number;
  bookedMinutes: number;
  bookingCost: number;
  loggedMinutes: number;
  loggedCost: number;
  versionCost: number;
};

type DepartmentGroup = {
  department: string;
  artists: ArtistRow[];
  artistCount: number;
  versionDaysTotal: number;
  bookedDaysTotal: number;
  taskBidDaysTotal: number;
  bookedCost: number;
  loggedCost: number;
  versionCost: number;
  combinedCost: number;
  billedValue: number;
};

type TimelineLaneItem = {
  id: string;
  startIndex: number;
  endIndex: number;
};

type BookingHoverCardState = {
  x: number;
  y: number;
  kind: "booking";
  artistName: string;
  department: string;
  booking: BookingRecord;
  bookingWorkingDays: number;
  elapsedWorkingDays: number;
  remainingWorkingDays: number;
  completedPercent: number;
};

type VersionHoverCardState = {
  x: number;
  y: number;
  kind: "version";
  artistName: string;
  department: string;
  date: string;
  versions: VersionMirrorRecord[];
};

type HoverCardState = BookingHoverCardState | VersionHoverCardState;

const ARTIST_COLUMN_WIDTH = 220;
const DEFAULT_DAY_COLUMN_WIDTH = 32;
const MIN_DAY_COLUMN_WIDTH = DEFAULT_DAY_COLUMN_WIDTH;
const MAX_DAY_COLUMN_WIDTH = 52;
const LANE_HEIGHT = 28;
const LANE_GAP = 4;
const VERSION_INDICATOR_HEIGHT = 10;
const VERSION_INDICATOR_GAP = 6;
const TIMELINE_VERTICAL_PADDING = 6;

const DEPARTMENT_COLOR_MAP: Record<string, string> = {
  "Animation": "#16d9f3",
  "Asset Build": "#ff3a1d",
  "Comp": "#1fdb3b",
  "Editorial": "#17c84a",
  "FX": "#ff1616",
  "Groom": "#8d14ff",
  "Graphics": "#bfbfbf",
  "Layout": "#5311b9",
  "Lighting": "#fff200",
  "Matchmove": "#ff8a00",
  "Matte Painting": "#1d57ff",
  "Production": "#f4a7f2",
  "Supervision": "#fff200",
  "Vendor": "#ff73b5",
  "Roto": "#ff1d91",
  "Lookdev": "#14dc2d",
  "Texture": "#7da100",
};

const DEFAULT_CURRENCY = "GBP";

type LocalTaskPipelineMapping = {
  appService: string;
  entityType: "shot" | "asset" | "project";
  departmentName?: string;
};

const DEFAULT_TASK_PIPELINE_MAPPINGS: LocalTaskPipelineMapping[] = [
  { appService: "supervisor", entityType: "shot", departmentName: "Production" },
  { appService: "concept", entityType: "shot", departmentName: "Matte Painting" },
  { appService: "dmp", entityType: "shot", departmentName: "Matte Painting" },
  { appService: "comp", entityType: "shot", departmentName: "Comp" },
  { appService: "roto", entityType: "shot", departmentName: "Vendor" },
  { appService: "prep", entityType: "shot", departmentName: "Vendor" },
  { appService: "gfx", entityType: "shot", departmentName: "Graphics" },
  { appService: "cameratr", entityType: "shot", departmentName: "Matchmove" },
  { appService: "objecttr", entityType: "shot", departmentName: "Matchmove" },
  { appService: "ranim", entityType: "shot", departmentName: "Matchmove" },
  { appService: "layout", entityType: "shot", departmentName: "Layout" },
  { appService: "anim", entityType: "shot", departmentName: "Animation" },
  { appService: "light", entityType: "shot", departmentName: "Lighting" },
  { appService: "fx", entityType: "shot", departmentName: "FX" },
  { appService: "review", entityType: "shot", departmentName: "Production" },
  { appService: "supervisor", entityType: "asset", departmentName: "Supervision" },
  { appService: "concept", entityType: "asset", departmentName: "Matte Painting" },
  { appService: "dmp", entityType: "asset", departmentName: "Matte Painting" },
  { appService: "model", entityType: "asset", departmentName: "Asset Build" },
  { appService: "rig", entityType: "asset", departmentName: "Asset Build" },
  { appService: "retop", entityType: "asset", departmentName: "Asset Build" },
  { appService: "cloth", entityType: "asset", departmentName: "Asset Build" },
  { appService: "fur", entityType: "asset", departmentName: "Groom" },
  { appService: "groom", entityType: "asset", departmentName: "Groom" },
  { appService: "texture", entityType: "asset", departmentName: "Asset Build" },
  { appService: "anim", entityType: "asset", departmentName: "Animation" },
  { appService: "fx", entityType: "asset", departmentName: "FX" },
  { appService: "lookdev", entityType: "asset", departmentName: "gen" },
  { appService: "photoscan", entityType: "asset", departmentName: "Asset Build" },
  { appService: "review", entityType: "asset", departmentName: "Supervision" },
  { appService: "supervisor", entityType: "project", departmentName: "Supervision" },
  { appService: "production", entityType: "project", departmentName: "Production" },
  { appService: "editorial", entityType: "project", departmentName: "Editorial" },
];

function toIsoDate(value?: string | null) {
  if (!value || typeof value !== "string") return undefined;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  if (!match) return undefined;
  return match[1];
}

function clampRange(startDate: string, endDate: string, visibleStart: string, visibleEnd: string) {
  const start = startDate < visibleStart ? visibleStart : startDate;
  const end = endDate > visibleEnd ? visibleEnd : endDate;
  if (end < start) return null;
  return { start, end };
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "").trim();
  if (normalized.length !== 6) return { r: 148, g: 163, b: 184 };
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function rgba(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mixWithWhite(hex: string, colorWeight: number) {
  const { r, g, b } = hexToRgb(hex);
  const weight = Math.max(0, Math.min(1, colorWeight));
  const mix = (channel: number) => Math.round(255 - (255 - channel) * weight);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function mixedRgba(hex: string, colorWeight: number, alpha: number) {
  const { r, g, b } = hexToRgb(hex);
  const weight = Math.max(0, Math.min(1, colorWeight));
  const mix = (channel: number) => Math.round(255 - (255 - channel) * weight);
  return `rgba(${mix(r)}, ${mix(g)}, ${mix(b)}, ${alpha})`;
}

function normalizeTaskKey(value?: string | null) {
  return (value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function normalizeDepartmentName(value?: string | null) {
  const cleaned = (value || "").trim();
  if (!cleaned) return "Unassigned";
  if (cleaned.endsWith(" Active")) return cleaned.replace(/\s+Active$/i, "").trim() || "Unassigned";
  return cleaned;
}

function getDepartmentColor(department?: string | null) {
  const normalized = normalizeDepartmentName(department);
  return DEPARTMENT_COLOR_MAP[normalized] || "#94a3b8";
}

function buildArtistLabel(person?: Partial<Person>) {
  return person?.displayName || person?.fullName || person?.name || person?.email || "Unknown artist";
}

function formatDayCountLabel(days: number) {
  const rounded = Math.round(days * 10) / 10;
  const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${label} day${rounded === 1 ? "" : "s"}`;
}

function assignTimelineLanes(items: TimelineLaneItem[]) {
  const laneEndIndices: number[] = [];
  const laneById = new Map<string, number>();

  [...items]
    .sort((a, b) => {
      if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
      if (a.endIndex !== b.endIndex) return a.endIndex - b.endIndex;
      return a.id.localeCompare(b.id);
    })
    .forEach((item) => {
      let laneIndex = laneEndIndices.findIndex((endIndex) => item.startIndex > endIndex);
      if (laneIndex === -1) {
        laneIndex = laneEndIndices.length;
        laneEndIndices.push(item.endIndex);
      } else {
        laneEndIndices[laneIndex] = item.endIndex;
      }
      laneById.set(item.id, laneIndex);
    });

  return {
    laneById,
    laneCount: Math.max(laneEndIndices.length, 1),
  };
}

function countWorkingDays(startDate?: string, endDate?: string) {
  if (!startDate || !endDate) return 0;
  return getDateRangeDays(startDate, endDate).filter((date) => !isWeekend(date)).length;
}

function countWorkingDaysUntil(startDate?: string, endDate?: string, comparisonDate?: string) {
  if (!startDate || !endDate || !comparisonDate) return 0;
  return getDateRangeDays(startDate, endDate)
    .filter((date) => !isWeekend(date) && date <= comparisonDate)
    .length;
}

function initialsForName(name: string) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("");
}

function buildVisibleMonthMarkers(days: string[]) {
  const markers: Array<{ key: string; label: string; startIndex: number; span: number }> = [];
  days.forEach((day, index) => {
    const date = new Date(`${day}T00:00:00.000Z`);
    if (!Number.isFinite(date.getTime())) return;

    const currentKey = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    const previousDay = index > 0 ? new Date(`${days[index - 1]}T00:00:00.000Z`) : null;
    const previousKey =
      previousDay && Number.isFinite(previousDay.getTime())
        ? `${previousDay.getUTCFullYear()}-${previousDay.getUTCMonth()}`
        : null;

    if (currentKey === previousKey) return;

    markers.push({
      key: currentKey,
      label: date.toLocaleDateString("en-GB", { month: "long" }),
      startIndex: index,
      span: 1,
    });
  });

  markers.forEach((marker, index) => {
    const nextMarker = markers[index + 1];
    const endIndex = nextMarker ? nextMarker.startIndex : days.length;
    marker.span = Math.max(endIndex - marker.startIndex, 1);
  });

  return markers;
}

function buildProjectDateRangeFromVersions(
  versions: VersionMirrorRecord[],
  projectId: string,
  fptProjectId?: string | null
) {
  let earliest: string | null = null;
  let latest: string | null = null;

  versions.forEach((version) => {
    if (version.isDeleted) return;
    const matchesLocalProject = version.projectId && String(version.projectId) === String(projectId);
    const matchesFptProject = fptProjectId && version.fptProjectId && String(version.fptProjectId) === String(fptProjectId);
    if (!matchesLocalProject && !matchesFptProject) return;

    const day = toIsoDate(version.publishDateNormalized) || toIsoDate(version.createdAtSource) || toIsoDate(version.ingestedAt);
    if (!day) return;

    if (!earliest || day < earliest) earliest = day;
    if (!latest || day > latest) latest = day;
  });

  return earliest && latest ? { from: earliest, to: latest } : null;
}

function versionMatchesProject(version: VersionMirrorRecord, projectId: string, fptProjectId?: string | null) {
  const matchesLocalProject = version.projectId && String(version.projectId) === String(projectId);
  const matchesFptProject = fptProjectId && version.fptProjectId && String(version.fptProjectId) === String(fptProjectId);
  return Boolean(matchesLocalProject || matchesFptProject);
}

function formatDateForDisplay(value?: string, dateFormat: DateDisplayFormat = "dd-mm-yyyy") {
  if (!value) return "";
  const normalized = toIsoDate(value);
  if (!normalized) return value;
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  if (dateFormat === "yyyy-mm-dd") return `${year}-${month}-${day}`;
  if (dateFormat === "dd/mm/yyyy") return `${day}/${month}/${year}`;
  return `${day}-${month}-${year}`;
}

function formatDateForBookingBar(value?: string) {
  if (!value) return "";
  const normalized = toIsoDate(value);
  if (!normalized) return value;
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  const monthIndex = Number(month) - 1;
  const shortMonth = Number.isFinite(monthIndex)
    ? new Date(Date.UTC(Number(year), monthIndex, Number(day))).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })
    : month;
  return `${day} ${shortMonth} ${year}`;
}

function formatVersionEntityLabel(version: VersionMirrorRecord) {
  const linkedEntityType = String(version.linkedEntityType || "").trim();
  const linkedEntityName = String(version.linkedEntityName || "").trim();
  const taskId = String(version.taskId || "").trim();
  const pipelineStep = String(version.pipelineStep || "").trim();

  if (linkedEntityType && linkedEntityName) return `${linkedEntityType}: ${linkedEntityName}`;
  if (linkedEntityName) return linkedEntityName;
  if (taskId && pipelineStep) return `${pipelineStep} · Task ${taskId}`;
  if (taskId) return `Task ${taskId}`;
  if (pipelineStep) return pipelineStep;
  return "Unlinked entity";
}

function classifyVersionsForDay(
  versions: VersionMirrorRecord[],
  projectId: string,
  fptProjectId?: string | null,
): "current" | "other" | "mixed" | null {
  if (!versions.length) return null;
  let hasCurrent = false;
  let hasOther = false;
  versions.forEach((version) => {
    if (versionMatchesProject(version, projectId, fptProjectId)) {
      hasCurrent = true;
    } else {
      hasOther = true;
    }
  });
  if (hasCurrent && hasOther) return "mixed";
  if (hasCurrent) return "current";
  if (hasOther) return "other";
  return null;
}

function getVersionIndicatorColor(
  classification: "current" | "other" | "mixed" | null,
  departmentColor: string,
) {
  switch (classification) {
    case "current":
      return mixedRgba(departmentColor, 0.5, 1);
    case "mixed":
      return mixedRgba(departmentColor, 0.2, 1);
    case "other":
      return "rgba(148, 163, 184, 0.5)";
    default:
      return "transparent";
  }
}

export function ProjectUtilisation({
  projectId,
  fptProjectId,
  projectName,
  projectStartDate,
  projectEndDate,
  shots = [],
  assets = [],
  rates,
  assetRates,
  supervisionPercentage = 0,
  currency = DEFAULT_CURRENCY,
}: ProjectUtilisationProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState<Person[]>([]);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [versions, setVersions] = useState<VersionMirrorRecord[]>([]);
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [fromDate, setFromDate] = useState<string>(() => {
    const projectStart = toIsoDate(projectStartDate);
    return projectStart || new Date().toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState<string>(() => {
    const projectEnd = toIsoDate(projectEndDate);
    return projectEnd || new Date().toISOString().slice(0, 10);
  });
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [groupMode, setGroupMode] = useState<GroupMode>("department");
  const [artistSearch, setArtistSearch] = useState("");
  const [dayColumnWidth, setDayColumnWidth] = useState(DEFAULT_DAY_COLUMN_WIDTH);
  const [hoverCard, setHoverCard] = useState<HoverCardState | null>(null);
  const [dateDisplayFormat, setDateDisplayFormat] = useState<DateDisplayFormat>("dd-mm-yyyy");
  const [collapsedDepartments, setCollapsedDepartments] = useState<Record<string, boolean>>({});
  const [syncingVersions, setSyncingVersions] = useState(false);
  const hasUserEditedDateRangeRef = useRef(false);

  const projectDateRangeFromVersions = useMemo(
    () => buildProjectDateRangeFromVersions(versions, projectId, fptProjectId),
    [versions, projectId, fptProjectId]
  );

  const defaultProjectDateRange = useMemo(() => {
    const projectStart = toIsoDate(projectStartDate);
    const projectEnd = toIsoDate(projectEndDate);
    if (projectStart && projectEnd) {
      return { from: projectStart, to: projectEnd };
    }
    return projectDateRangeFromVersions;
  }, [projectEndDate, projectStartDate, projectDateRangeFromVersions]);

  const loadVersions = React.useCallback(async (headers: Record<string, string>) => {
    const response = await fetch("/api/versions", { headers });
    return response.ok ? response.json() : [];
  }, []);

  const loadBookings = React.useCallback(async (headers: Record<string, string>) => {
    const response = await fetch("/api/bookings", { headers });
    return response.ok ? response.json() : [];
  }, []);

  useEffect(() => {
    if (!user) return;
    let isCancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const headers = { "x-user-uid": user.uid };
        const [peopleRes, bookingsData, versionsData, timeLogsRes] = await Promise.all([
          fetch("/api/people", { headers }),
          loadBookings(headers),
          loadVersions(headers),
          fetch("/api/time-logs", { headers }),
        ]);

        const preferencesRes = fetch("/api/user-preferences", { headers });
        const [peopleData, timeLogsData, preferencesPayload] = await Promise.all([
          peopleRes.ok ? peopleRes.json() : [],
          timeLogsRes.ok ? timeLogsRes.json() : [],
          preferencesRes.then(async (res) => (res.ok ? res.json() : {})).catch(() => ({})),
        ]);

        if (isCancelled) return;
        setPeople(Array.isArray(peopleData) ? peopleData : []);
        setBookings(Array.isArray(bookingsData) ? bookingsData : []);
        setVersions(Array.isArray(versionsData) ? versionsData : []);
        setTimeLogs(Array.isArray(timeLogsData) ? timeLogsData : []);
        const savedDateFormat = preferencesPayload?.preferences?.settings?.display?.dateFormat;
        if (savedDateFormat && ["dd-mm-yyyy", "dd/mm/yyyy", "yyyy-mm-dd"].includes(savedDateFormat)) {
          setDateDisplayFormat(savedDateFormat as DateDisplayFormat);
        }
      } catch (error) {
        console.error("Failed to load project utilisation data", error);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isCancelled = true;
    };
  }, [loadBookings, loadVersions, user]);

  useEffect(() => {
    hasUserEditedDateRangeRef.current = false;
    setCollapsedDepartments({});
  }, [projectId, fptProjectId]);

  useEffect(() => {
    if (!defaultProjectDateRange || hasUserEditedDateRangeRef.current) return;
    setFromDate(defaultProjectDateRange.from);
    setToDate(defaultProjectDateRange.to);
  }, [defaultProjectDateRange]);

  const visibleDays = useMemo(() => {
    if (!fromDate || !toDate || toDate < fromDate) return [];
    return getDateRangeDays(fromDate, toDate);
  }, [fromDate, toDate]);

  const personById = useMemo(() => new Map(people.map((person) => [String(person.id), person])), [people]);
  const personByFptPersonId = useMemo(
    () => new Map(people.filter((person) => person.fptPersonId).map((person) => [String(person.fptPersonId), person])),
    [people]
  );
  const personByName = useMemo(
    () =>
      new Map(
        people
          .map((person) => [buildArtistLabel(person).trim().toLowerCase(), person] as const)
          .filter(([name]) => Boolean(name))
      ),
    [people]
  );

  const projectBookings = useMemo(() => {
    return bookings.filter((booking) => {
      if (booking.isDeleted) return false;
      const matchesLocalProject = booking.projectId && String(booking.projectId) === String(projectId);
      const matchesFptProject = fptProjectId && booking.fptProjectId && String(booking.fptProjectId) === String(fptProjectId);
      if (!matchesLocalProject && !matchesFptProject) return false;
      if (!booking.startDate || !booking.endDate) return false;
      return booking.endDate >= fromDate && booking.startDate <= toDate;
    });
  }, [bookings, fromDate, fptProjectId, projectId, toDate]);

  const allProjectVersions = useMemo(() => {
    return versions.filter((version) => {
      if (version.isDeleted) return false;
      return versionMatchesProject(version, projectId, fptProjectId);
    });
  }, [fptProjectId, projectId, versions]);

  const projectVersions = useMemo(() => {
    return versions.filter((version) => {
      if (version.isDeleted) return false;
      if (!versionMatchesProject(version, projectId, fptProjectId)) return false;
      const day = toIsoDate(version.publishDateNormalized) || toIsoDate(version.createdAtSource) || toIsoDate(version.ingestedAt);
      if (!day) return false;
      return day >= fromDate && day <= toDate;
    });
  }, [fptProjectId, fromDate, projectId, toDate, versions]);

  const projectTimeLogs = useMemo(() => {
    return timeLogs.filter((timeLog) => {
      if (String(timeLog.projectId || "") !== String(projectId)) return false;
      const day = toIsoDate(timeLog.logDate);
      if (!day) return false;
      return day >= fromDate && day <= toDate;
    });
  }, [fromDate, projectId, timeLogs, toDate]);

  const loggedCostByPersonId = useMemo(() => {
    const next = new Map<string, { minutes: number; cost: number }>();
    projectTimeLogs.forEach((timeLog) => {
      if (!timeLog.personId) return;
      const key = String(timeLog.personId);
      const person = personById.get(key);
      const minutes = Number(timeLog.minutes || 0);
      const fallbackRate = Number(person?.dailyRate || 0);
      const cost =
        typeof timeLog.cost === "number" && Number.isFinite(timeLog.cost)
          ? Number(timeLog.cost)
          : fallbackRate > 0
            ? (minutes / 480) * fallbackRate
            : 0;
      const existing = next.get(key) || { minutes: 0, cost: 0 };
      existing.minutes += minutes;
      existing.cost += cost;
      next.set(key, existing);
    });
    return next;
  }, [personById, projectTimeLogs]);

  const departmentBidRollups = useMemo(() => {
    const shotDepartmentMap = new Map<string, string>();
    const assetDepartmentMap = new Map<string, string>();
    DEFAULT_TASK_PIPELINE_MAPPINGS.forEach((mapping) => {
      if (mapping.entityType === "shot") {
        shotDepartmentMap.set(normalizeTaskKey(mapping.appService), normalizeDepartmentName(mapping.departmentName));
      } else if (mapping.entityType === "asset") {
        assetDepartmentMap.set(normalizeTaskKey(mapping.appService), normalizeDepartmentName(mapping.departmentName));
      }
    });

    const costTotals = new Map<string, number>();
    const dayTotals = new Map<string, number>();
    const addCost = (department: string, value: number) => {
      if (!Number.isFinite(value) || value <= 0) return;
      costTotals.set(department, (costTotals.get(department) || 0) + value);
    };
    const addDays = (department: string, value: number) => {
      if (!Number.isFinite(value) || value <= 0) return;
      dayTotals.set(department, (dayTotals.get(department) || 0) + value);
    };

    shots
      .filter((shot) => shot && isBidItemIncluded(shot))
      .forEach((shot) => {
        const quantity = Math.max(1, Number(shot.quantity || 1));
        let totalTaskDays = 0;
        Object.entries(shot.days || {}).forEach(([taskType, taskDays]) => {
          const numericDays = Number(taskDays || 0);
          if (!numericDays) return;
          if (normalizeTaskKey(taskType) === "supervisor") return;
          totalTaskDays += numericDays;
          const department = shotDepartmentMap.get(normalizeTaskKey(taskType));
          const rate = Number(rates?.[taskType as keyof RateCard] || 0);
          if (!department || !rate) return;
          addCost(department, numericDays * rate * quantity);
          addDays(department, numericDays * quantity);
        });

        const supervisionDays = totalTaskDays * (Number(supervisionPercentage || 0) / 100);
        const supervisionRate = Number(rates?.supervisor || 0);
        const supervisionDepartment = shotDepartmentMap.get("supervisor") || "Production";
        addCost(supervisionDepartment, supervisionDays * supervisionRate * quantity);
        addDays(supervisionDepartment, supervisionDays * quantity);
      });

    assets
      .filter((asset) => asset && isBidItemIncluded(asset))
      .forEach((asset) => {
        const quantity = Math.max(1, Number(asset.quantity || 1));
        let totalTaskDays = 0;
        Object.entries(asset.days || {}).forEach(([taskType, taskDays]) => {
          const numericDays = Number(taskDays || 0);
          if (!numericDays) return;
          if (normalizeTaskKey(taskType) === "supervisor") return;
          totalTaskDays += numericDays;
          const department = assetDepartmentMap.get(normalizeTaskKey(taskType));
          const rate = Number(assetRates?.[taskType as keyof AssetRateCard] || 0);
          if (!department || !rate) return;
          addCost(department, numericDays * rate * quantity);
          addDays(department, numericDays * quantity);
        });

        const supervisionDays = totalTaskDays * (Number(supervisionPercentage || 0) / 100);
        const supervisionRate = Number(assetRates?.supervisor || 0);
        const supervisionDepartment = assetDepartmentMap.get("supervisor") || "Supervision";
        addCost(supervisionDepartment, supervisionDays * supervisionRate * quantity);
        addDays(supervisionDepartment, supervisionDays * quantity);
      });

    return { costTotals, dayTotals };
  }, [assetRates, assets, rates, shots, supervisionPercentage]);

  const allKnownArtists = useMemo(() => {
    const rowMap = new Map<string, ArtistRow>();

    people
      .filter((person) => person.fptPersonId || person.fptUserLinked || person.directoryType !== "external")
      .forEach((person) => {
        rowMap.set(String(person.id), {
          id: String(person.id),
          name: buildArtistLabel(person),
          department: normalizeDepartmentName(person.department),
          roleTitle: person.roleTitle || person.title || person.role,
          photoUrl: person.photoUrl,
          fptPersonId: person.fptPersonId,
          bookings: [],
          versionDays: [],
          versionDaySet: new Set<string>(),
          versionCount: 0,
          unbookedVersionDays: [],
          versionsByDay: new Map<string, VersionMirrorRecord[]>(),
          allVersionsByDay: new Map<string, VersionMirrorRecord[]>(),
          bookedDays: 0,
          bookedWorkingDays: 0,
          bookedMinutes: 0,
          bookingCost: 0,
          loggedMinutes: 0,
          loggedCost: 0,
          versionCost: 0,
        });
      });

    projectBookings.forEach((booking) => {
      const person =
        (booking.personId && personById.get(String(booking.personId))) ||
        (booking.fptPersonId && personByFptPersonId.get(String(booking.fptPersonId))) ||
        (booking.personName && personByName.get(booking.personName.trim().toLowerCase()));
      const key = person ? String(person.id) : `booking:${booking.fptPersonId || booking.fptUserId || booking.personName || booking.id}`;
      const existing = rowMap.get(key);
      const nextRow: ArtistRow = existing || {
        id: key,
        name: buildArtistLabel(person) || booking.personName || "Unresolved artist",
        department: normalizeDepartmentName(person?.department || booking.department),
        roleTitle: person?.roleTitle || person?.title || person?.role,
        photoUrl: person?.photoUrl,
        fptPersonId: person?.fptPersonId || booking.fptPersonId,
        fptUserId: booking.fptUserId,
        bookings: [],
        versionDays: [],
        versionDaySet: new Set<string>(),
        versionCount: 0,
        unbookedVersionDays: [],
        versionsByDay: new Map<string, VersionMirrorRecord[]>(),
        allVersionsByDay: new Map<string, VersionMirrorRecord[]>(),
        bookedDays: 0,
        bookedWorkingDays: 0,
        bookedMinutes: 0,
        bookingCost: 0,
        loggedMinutes: 0,
        loggedCost: 0,
        versionCost: 0,
      };
      nextRow.bookings.push(booking);
      rowMap.set(key, nextRow);
    });

    projectVersions.forEach((version) => {
      const person =
        (version.personId && personById.get(String(version.personId))) ||
        (version.fptPersonId && personByFptPersonId.get(String(version.fptPersonId))) ||
        (version.artistName && personByName.get(version.artistName.trim().toLowerCase()));
      const key = person ? String(person.id) : `version:${version.fptPersonId || version.fptUserId || version.artistName || version.id}`;
      const existing = rowMap.get(key);
      const nextRow: ArtistRow = existing || {
        id: key,
        name: buildArtistLabel(person) || version.artistName || "Unresolved artist",
        department: normalizeDepartmentName(person?.department || version.department || version.pipelineStep),
        roleTitle: person?.roleTitle || person?.title || person?.role,
        photoUrl: person?.photoUrl,
        fptPersonId: person?.fptPersonId || version.fptPersonId,
        fptUserId: version.fptUserId,
        bookings: [],
        versionDays: [],
        versionDaySet: new Set<string>(),
        versionCount: 0,
        unbookedVersionDays: [],
        versionsByDay: new Map<string, VersionMirrorRecord[]>(),
        allVersionsByDay: new Map<string, VersionMirrorRecord[]>(),
        bookedDays: 0,
        bookedWorkingDays: 0,
        bookedMinutes: 0,
        bookingCost: 0,
        loggedMinutes: 0,
        loggedCost: 0,
        versionCost: 0,
      };
      const day = toIsoDate(version.publishDateNormalized) || toIsoDate(version.createdAtSource) || toIsoDate(version.ingestedAt);
      if (day) {
        nextRow.versionDaySet.add(day);
        const existingVersionsForDay = nextRow.versionsByDay.get(day) || [];
        existingVersionsForDay.push(version);
        nextRow.versionsByDay.set(day, existingVersionsForDay);
      }
      nextRow.versionCount += 1;
      rowMap.set(key, nextRow);
    });

    versions.forEach((version) => {
      const person =
        (version.personId && personById.get(String(version.personId))) ||
        (version.fptPersonId && personByFptPersonId.get(String(version.fptPersonId))) ||
        (version.artistName && personByName.get(version.artistName.trim().toLowerCase()));
      const key = person ? String(person.id) : `version:${version.fptPersonId || version.fptUserId || version.artistName || version.id}`;
      const existing = rowMap.get(key);
      const nextRow: ArtistRow = existing || {
        id: key,
        name: buildArtistLabel(person) || version.artistName || "Unresolved artist",
        department: normalizeDepartmentName(person?.department || version.department || version.pipelineStep),
        roleTitle: person?.roleTitle || person?.title || person?.role,
        photoUrl: person?.photoUrl,
        fptPersonId: person?.fptPersonId || version.fptPersonId,
        fptUserId: version.fptUserId,
        bookings: [],
        versionDays: [],
        versionDaySet: new Set<string>(),
        versionCount: 0,
        unbookedVersionDays: [],
        versionsByDay: new Map<string, VersionMirrorRecord[]>(),
        allVersionsByDay: new Map<string, VersionMirrorRecord[]>(),
        bookedDays: 0,
        bookedWorkingDays: 0,
        bookedMinutes: 0,
        bookingCost: 0,
        loggedMinutes: 0,
        loggedCost: 0,
        versionCost: 0,
      };
      const day = toIsoDate(version.publishDateNormalized) || toIsoDate(version.createdAtSource) || toIsoDate(version.ingestedAt);
      if (day) {
        const existingVersionsForDay = nextRow.allVersionsByDay.get(day) || [];
        existingVersionsForDay.push(version);
        nextRow.allVersionsByDay.set(day, existingVersionsForDay);
      }
      rowMap.set(key, nextRow);
    });

    return Array.from(rowMap.values())
      .map((row) => {
        const versionDays = Array.from(row.versionDaySet).sort();
        const bookingDaySet = new Set<string>();
        let bookedMinutes = 0;
        let bookingCost = 0;
        row.bookings.forEach((booking) => {
          const visibleRange = clampRange(booking.startDate || "", booking.endDate || "", fromDate, toDate);
          if (!visibleRange) return;
          getDateRangeDays(visibleRange.start, visibleRange.end).forEach((date) => bookingDaySet.add(date));
          bookedMinutes += Number(booking.bookedMinutes || booking.durationMinutes || 0);
          bookingCost += Number(booking.bookedLabourCost || 0);
        });

        const unbookedVersionDays = versionDays.filter((date) => !bookingDaySet.has(date));
        const logged = loggedCostByPersonId.get(String(row.id));
        const versionCost =
          versionDays.length > 0 && Number.isFinite(Number(personById.get(String(row.id))?.dailyRate || 0))
            ? versionDays.length * Number(personById.get(String(row.id))?.dailyRate || 0)
            : 0;
        return {
          ...row,
          bookings: [...row.bookings].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || "") || a.id.localeCompare(b.id)),
          versionDays,
          unbookedVersionDays,
          versionsByDay: row.versionsByDay,
          allVersionsByDay: row.allVersionsByDay,
          bookedDays: bookingDaySet.size,
          bookedWorkingDays: bookingDaySet.size,
          bookedMinutes,
          bookingCost,
          loggedMinutes: Number(logged?.minutes || 0),
          loggedCost: Number(logged?.cost || 0),
          versionCost,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [fromDate, loggedCostByPersonId, people, personByFptPersonId, personById, personByName, projectBookings, projectVersions, toDate, versions]);

  const filteredArtists = useMemo(() => {
    const searchNeedle = artistSearch.trim().toLowerCase();
    return allKnownArtists.filter((artist) => {
      if (departmentFilter !== "all" && artist.department !== departmentFilter) return false;
      if (artist.bookings.length === 0 && artist.versionDays.length === 0) return false;
      if (!searchNeedle) return true;
      return (
        artist.name.toLowerCase().includes(searchNeedle) ||
        artist.department.toLowerCase().includes(searchNeedle) ||
        (artist.roleTitle || "").toLowerCase().includes(searchNeedle)
      );
    });
  }, [allKnownArtists, artistSearch, departmentFilter]);

  const departmentOptions = useMemo(() => {
    return Array.from(new Set(allKnownArtists.map((artist) => artist.department))).sort((a, b) => a.localeCompare(b));
  }, [allKnownArtists]);

  const groupedArtists = useMemo<DepartmentGroup[]>(() => {
    if (groupMode === "flat") {
      const bookedCost = filteredArtists.reduce((sum, artist) => sum + artist.bookingCost, 0);
      const loggedCost = filteredArtists.reduce((sum, artist) => sum + artist.loggedCost, 0);
      const versionCost = filteredArtists.reduce((sum, artist) => sum + artist.versionCost, 0);
      return [
        {
          department: "All artists",
          artists: filteredArtists,
          artistCount: filteredArtists.length,
          versionDaysTotal: filteredArtists.reduce((sum, artist) => sum + artist.versionDays.length, 0),
          bookedDaysTotal: filteredArtists.reduce((sum, artist) => sum + artist.bookedDays, 0),
          taskBidDaysTotal: Array.from(departmentBidRollups.dayTotals.values()).reduce((sum, value) => sum + value, 0),
          bookedCost,
          loggedCost,
          versionCost,
          combinedCost: Math.max(bookedCost, loggedCost, versionCost),
          billedValue: 0,
        },
      ];
    }

    const map = new Map<string, ArtistRow[]>();
    filteredArtists.forEach((artist) => {
      const bucket = map.get(artist.department) || [];
      bucket.push(artist);
      map.set(artist.department, bucket);
    });

    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([department, artists]) => {
        const bookedCost = artists.reduce((sum, artist) => sum + artist.bookingCost, 0);
        const loggedCost = artists.reduce((sum, artist) => sum + artist.loggedCost, 0);
        const versionCost = artists.reduce((sum, artist) => sum + artist.versionCost, 0);
        return {
          department,
          artists,
          artistCount: artists.length,
          versionDaysTotal: artists.reduce((sum, artist) => sum + artist.versionDays.length, 0),
          bookedDaysTotal: artists.reduce((sum, artist) => sum + artist.bookedDays, 0),
          taskBidDaysTotal: departmentBidRollups.dayTotals.get(department) || 0,
          bookedCost,
          loggedCost,
          versionCost,
          combinedCost: Math.max(bookedCost, loggedCost, versionCost),
          billedValue: departmentBidRollups.costTotals.get(department) || 0,
        };
      });
  }, [departmentBidRollups, filteredArtists, groupMode]);

  const visibleMonthMarkers = useMemo(() => buildVisibleMonthMarkers(visibleDays), [visibleDays]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const timelineWidth = visibleDays.length * dayColumnWidth;

  const toggleDepartmentCollapsed = (department: string) => {
    setCollapsedDepartments((current) => ({
      ...current,
      [department]: !current[department],
    }));
  };

  const handleSyncVersions = async () => {
    if (!user || !projectId) return;
    setSyncingVersions(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-user-uid": user.uid,
      };
      const bookingsResponse = await fetch(`/api/fpt/projects/${projectId}/sync/bookings`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });
      const bookingsPayload = await bookingsResponse.json().catch(() => ({}));
      if (!bookingsResponse.ok) {
        throw new Error(bookingsPayload?.error || "Failed to sync bookings for this project");
      }

      const versionsResponse = await fetch(`/api/fpt/projects/${projectId}/sync/versions`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });
      const versionsPayload = await versionsResponse.json().catch(() => ({}));
      if (!versionsResponse.ok) {
        throw new Error(versionsPayload?.error || "Failed to sync versions for this project");
      }
      const [nextBookings, nextVersions] = await Promise.all([
        loadBookings({ "x-user-uid": user.uid }),
        loadVersions({ "x-user-uid": user.uid }),
      ]);
      setBookings(Array.isArray(nextBookings) ? nextBookings : []);
      setVersions(Array.isArray(nextVersions) ? nextVersions : []);
      const syncedBookingCount = Number(versionsPayload?.result?.bookingSync?.syncedCount || bookingsPayload?.syncedCount || 0);
      const syncedVersionCount = Number(versionsPayload?.result?.syncedCount || 0);
      const teamLinkedCount = Number(versionsPayload?.result?.teamLinkedCount || 0);
      const teamMessage = teamLinkedCount > 0 ? ` Added ${teamLinkedCount} observed artist${teamLinkedCount === 1 ? "" : "s"} to the Team tab.` : "";
      toast.success(`Synced ${syncedBookingCount} booking${syncedBookingCount === 1 ? "" : "s"} and ${syncedVersionCount} version${syncedVersionCount === 1 ? "" : "s"} for ${projectName || "this project"}.${teamMessage}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to sync FPT data for this project");
    } finally {
      setSyncingVersions(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-0">
      <section className="app-card rounded-b-none border border-b-0 border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-col gap-2.5 p-3.5">
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    <Layers3 className="h-3.5 w-3.5" />
                    Utilisation
                  </div>
                  <p className="max-w-4xl text-sm leading-5 text-slate-600 dark:text-slate-400">
                    View planned project usage from Flow Production Tracking bookings, then compare it with version activity to see who was booked, who visibly worked, and where activity drifted outside plan.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-slate-600 dark:text-slate-400 xl:justify-end xl:pr-4">
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <span className="h-3 w-3 rounded-sm border border-[#ffcc01]/60 bg-[#ffcc01]/15 dark:border-[#ffcc01]/50 dark:bg-[#ffcc01]/20" />
                    Booked
                  </span>
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <span className="h-3 w-3 rounded-sm bg-[#ffcc01]/60 dark:bg-[#ffcc01]/65" />
                    Version day in booking
                  </span>
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <span className="h-2.5 w-5 rounded-[4px] bg-[#e6b801] dark:bg-[#ffcc01]" />
                    Version outside booking
                  </span>
                </div>
              </div>
            </div>

          <div className="flex flex-col gap-2 xl:flex-row xl:items-end xl:justify-between">
            <div className="flex max-w-[34rem] flex-1 flex-col gap-2 xl:flex-row xl:items-center">
              <div className="relative max-w-[26rem] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <input
                  type="text"
                  value={artistSearch}
                  onChange={(event) => setArtistSearch(event.target.value)}
                  placeholder="Search artists or departments"
                  className="app-control w-full rounded-lg border border-slate-300 bg-white py-1.5 pl-10 pr-3 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
                />
              </div>
              <label className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-100">
                <span className="text-base font-semibold leading-none text-slate-500 dark:text-slate-400">−</span>
                <input
                  type="range"
                  min={MIN_DAY_COLUMN_WIDTH}
                  max={MAX_DAY_COLUMN_WIDTH}
                  value={dayColumnWidth}
                  onChange={(event) => setDayColumnWidth(Number(event.target.value))}
                  className="utilisation-zoom-slider"
                />
                <span className="text-base font-semibold leading-none text-slate-500 dark:text-slate-400">+</span>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-5 xl:pr-4">
              <label className="space-y-1">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">From</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => {
                    hasUserEditedDateRangeRef.current = true;
                    setFromDate(event.target.value);
                  }}
                  className="app-control w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark]"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">To</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => {
                    hasUserEditedDateRangeRef.current = true;
                    setToDate(event.target.value);
                  }}
                  className="app-control w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark]"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Department</span>
                <select
                  value={departmentFilter}
                  onChange={(event) => setDepartmentFilter(event.target.value)}
                  className="app-control w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                >
                  <option value="all">All departments</option>
                  {departmentOptions.map((department) => (
                    <option key={department} value={department}>
                      {department}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Group</span>
                <select
                  value={groupMode}
                  onChange={(event) => setGroupMode(event.target.value as GroupMode)}
                  className="app-control w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                >
                  <option value="department">Group by department</option>
                  <option value="flat">Flat artist list</option>
                </select>
              </label>
              {fptProjectId ? (
                <div className="flex flex-col items-end space-y-1 justify-self-end">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-transparent select-none">Sync</span>
                  <button
                    type="button"
                    onClick={handleSyncVersions}
                    disabled={syncingVersions}
                    title="This pulls bookings and version data from FPT - it will not write anything back or replace anything in FPT"
                    className="inline-flex items-center justify-center gap-1.5 self-start rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-1.5 text-xs font-medium text-slate-900 shadow-sm transition-colors hover:bg-[#e6b801] disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#ffcc01] dark:bg-[#ffcc01] dark:text-slate-900 dark:hover:bg-[#e6b801]"
                  >
                    <RefreshCw className={cn("h-4 w-4", syncingVersions && "animate-spin")} />
                    {syncingVersions ? "Syncing FPT..." : "Sync FPT"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="app-card rounded-t-none border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        {loading ? (
          <div className="p-10 text-sm text-slate-500 dark:text-slate-400">Loading utilisation timeline…</div>
        ) : visibleDays.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 dark:text-slate-400">Choose a valid date range to see project utilisation.</div>
        ) : filteredArtists.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 dark:text-slate-400">No artists match the current filters.</div>
        ) : (
          <div className="overflow-auto">
            <div className="min-w-max pb-8">
              <div className="sticky top-0 z-20 bg-white dark:bg-slate-900">
                <div className="flex border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div
                    className="sticky left-0 z-30 flex h-10 items-center border-r border-slate-200 bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
                    style={{ width: ARTIST_COLUMN_WIDTH, minWidth: ARTIST_COLUMN_WIDTH }}
                  >
                    Timeline
                  </div>
                  <div className="flex border-b border-slate-100 dark:border-slate-800" style={{ width: visibleDays.length * dayColumnWidth, minWidth: visibleDays.length * dayColumnWidth }}>
                    {visibleMonthMarkers.map((marker) => (
                      <div
                        key={marker.key}
                        className="flex h-10 items-center border-r border-slate-200 px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:border-slate-700 dark:text-slate-400"
                        style={{ width: marker.span * dayColumnWidth }}
                      >
                        {marker.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div
                    className="sticky left-0 z-30 flex h-10 items-center border-r border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    style={{ width: ARTIST_COLUMN_WIDTH, minWidth: ARTIST_COLUMN_WIDTH }}
                  >
                    Artist
                  </div>
                  <div className="flex">
                    {visibleDays.map((day) => (
                      <div
                        key={day}
                        className={cn(
                          "flex h-10 flex-col items-center justify-center border-r border-slate-200 px-1 text-center text-[10px] font-semibold uppercase tracking-[0.14em]",
                          isWeekend(day) ? "bg-slate-300/55 text-slate-500 dark:bg-slate-700/90 dark:text-slate-400" : "text-slate-500 dark:text-slate-400",
                          day === todayIso && "bg-amber-50 text-slate-800 dark:bg-amber-950/40 dark:text-amber-200"
                        )}
                        style={{ width: dayColumnWidth }}
                      >
                        <div>{new Date(`${day}T00:00:00.000Z`).toLocaleDateString("en-GB", { weekday: "short" })}</div>
                        <div>{day.slice(-2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {groupedArtists.map((group) => (
                <div key={group.department}>
                  {groupMode === "department" && (
                    <div className="flex border-y border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/60">
                      <div
                        className="sticky left-0 z-20 flex items-center gap-3 bg-slate-50/80 px-4 py-3 dark:bg-slate-800/60"
                        style={{ width: ARTIST_COLUMN_WIDTH, minWidth: ARTIST_COLUMN_WIDTH }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleDepartmentCollapsed(group.department)}
                          className="inline-flex h-5 w-5 items-center justify-center text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                          aria-label={`${collapsedDepartments[group.department] ? "Expand" : "Collapse"} ${group.department}`}
                        >
                          {collapsedDepartments[group.department] ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        <span className="inline-flex items-center justify-center text-slate-400 dark:text-slate-500">
                          <Users className="h-3 w-3" />
                        </span>
                        <span
                          className="app-pill inline-flex items-center whitespace-nowrap px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] leading-none"
                          style={{
                            backgroundColor: rgba(getDepartmentColor(group.department), 0.18),
                            color: "#0f172a",
                          }}
                        >
                          {group.department}
                        </span>
                        <span className="whitespace-nowrap text-sm font-medium text-slate-500 dark:text-slate-300">
                          {group.artistCount} artist{group.artistCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div
                        className="relative flex items-center"
                        style={{ width: timelineWidth, minWidth: timelineWidth }}
                      >
                        <div className="sticky right-0 z-10 ml-auto flex items-center gap-6 bg-slate-50/95 pl-6 pr-4 py-3 text-right backdrop-blur-sm dark:bg-slate-800/95">
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Days Worked (to date)</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {group.versionDaysTotal}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Total Booked Days</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {group.bookedDaysTotal}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Task Bid Days</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {Number.isInteger(group.taskBidDaysTotal) ? group.taskBidDaysTotal : group.taskBidDaysTotal.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Cost</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {formatCurrency(group.combinedCost, currency || DEFAULT_CURRENCY)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Total Bid Cost</span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {formatCurrency(group.billedValue, currency || DEFAULT_CURRENCY)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!collapsedDepartments[group.department] &&
                    group.artists.map((artist) => {
                    const timelineItems = artist.bookings
                      .map((booking) => {
                        const visibleRange = clampRange(booking.startDate || "", booking.endDate || "", fromDate, toDate);
                        if (!visibleRange) return null;
                        const startIndex = visibleDays.indexOf(visibleRange.start);
                        const endIndex = visibleDays.indexOf(visibleRange.end);
                        if (startIndex < 0 || endIndex < 0) return null;
                        return {
                          id: booking.id,
                          startIndex,
                          endIndex,
                        };
                      })
                      .filter(Boolean) as TimelineLaneItem[];

                    const { laneById, laneCount } = assignTimelineLanes(timelineItems);
                    const laneBlockHeight = Math.max(
                      laneCount * LANE_HEIGHT + Math.max(0, laneCount - 1) * LANE_GAP,
                      LANE_HEIGHT,
                    );
                    const timelineHeight =
                      TIMELINE_VERTICAL_PADDING +
                      laneBlockHeight +
                      VERSION_INDICATOR_GAP +
                      VERSION_INDICATOR_HEIGHT +
                      TIMELINE_VERTICAL_PADDING;
                    const laneBlockTop = TIMELINE_VERTICAL_PADDING;
                    const versionIndicatorTop = laneBlockTop + laneBlockHeight + VERSION_INDICATOR_GAP;
                    const departmentColor = getDepartmentColor(artist.department);

                    return (
                      <div key={artist.id} className="flex border-b border-slate-200 last:border-b-0 dark:border-slate-800">
                        <div
                          className="sticky left-0 z-10 flex border-r border-slate-200 bg-white px-4 py-1.5 dark:border-slate-700 dark:bg-slate-900"
                          style={{ width: ARTIST_COLUMN_WIDTH, minWidth: ARTIST_COLUMN_WIDTH }}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            {artist.photoUrl ? (
                              <img src={artist.photoUrl} alt={artist.name} className="app-avatar h-8 w-8 object-cover" />
                            ) : (
                              <div className="app-avatar inline-flex h-8 w-8 items-center justify-center bg-slate-100 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                                {initialsForName(artist.name)}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{artist.name}</div>
                              {artist.roleTitle ? <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{artist.roleTitle}</div> : null}
                            </div>
                          </div>
                        </div>

                        <div className="relative" style={{ height: timelineHeight, width: timelineWidth, minWidth: timelineWidth }}>
                          {visibleDays.map((day, index) => (
                            <div
                              key={day}
                              className={cn(
                                "absolute inset-y-0 border-r border-slate-100 dark:border-slate-800",
                                isWeekend(day) && "bg-slate-200/55 dark:bg-slate-700/75",
                                day === todayIso && "bg-amber-50/60 dark:bg-amber-950/30"
                              )}
                              style={{ left: index * dayColumnWidth, width: dayColumnWidth }}
                            />
                          ))}

                          {artist.bookings.map((booking) => {
                            const visibleRange = clampRange(booking.startDate || "", booking.endDate || "", fromDate, toDate);
                            if (!visibleRange) return null;
                            const startIndex = visibleDays.indexOf(visibleRange.start);
                            const endIndex = visibleDays.indexOf(visibleRange.end);
                            if (startIndex < 0 || endIndex < 0) return null;

                            const laneIndex = laneById.get(booking.id) || 0;
                            const width = (endIndex - startIndex + 1) * dayColumnWidth;
                            const left = startIndex * dayColumnWidth;
                            const top = laneBlockTop + laneIndex * (LANE_HEIGHT + LANE_GAP);
                            const dayRange = getDateRangeDays(visibleRange.start, visibleRange.end);
                            const bookingWorkingDays = countWorkingDays(booking.startDate, booking.endDate);
                            const elapsedWorkingDays = Math.min(
                              bookingWorkingDays,
                              countWorkingDaysUntil(booking.startDate, booking.endDate, todayIso)
                            );
                            const remainingWorkingDays = Math.max(0, bookingWorkingDays - elapsedWorkingDays);
                            const completedPercent = bookingWorkingDays > 0 ? Math.round((elapsedWorkingDays / bookingWorkingDays) * 100) : 0;
                            const bookingDateLabel = `${formatDateForBookingBar(booking.startDate)} - ${formatDateForBookingBar(booking.endDate)}`;
                            const bookingDaysLabel = `${bookingWorkingDays} days`;

                            return (
                              <div
                                key={booking.id}
                                className="app-control absolute overflow-hidden border shadow-sm dark:shadow-none"
                                style={{
                                  left,
                                  top,
                                  width,
                                  height: LANE_HEIGHT,
                                  borderWidth: 2,
                                  borderColor: mixedRgba(departmentColor, 0.8, 0.7),
                                  backgroundColor: "transparent",
                                }}
                                onMouseEnter={(event) => {
                                  setHoverCard({
                                    x: event.clientX,
                                    y: event.clientY,
                                    kind: "booking",
                                    artistName: artist.name,
                                    department: artist.department,
                                    booking,
                                    bookingWorkingDays,
                                    elapsedWorkingDays,
                                    remainingWorkingDays,
                                    completedPercent,
                                  });
                                }}
                                onMouseMove={(event) => {
                                  setHoverCard((current) =>
                                    current?.kind === "booking" && current.booking.id === booking.id
                                      ? { ...current, x: event.clientX, y: event.clientY }
                                      : current
                                  );
                                }}
                                onMouseLeave={() =>
                                  setHoverCard((current) =>
                                    current?.kind === "booking" && current.booking.id === booking.id ? null : current
                                  )
                                }
                              >
                                <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${dayRange.length}, minmax(0, 1fr))` }}>
                                  {dayRange.map((day) => {
                                    const dayVersions = artist.versionsByDay.get(day) || [];
                                    const hasVersionActivity = dayVersions.length > 0;
                                    return (
                                      <div
                                        key={day}
                                        className="border-r border-white/30 last:border-r-0"
                                        style={{
                                          backgroundColor: hasVersionActivity
                                            ? mixedRgba(departmentColor, 0.5, 0.7)
                                            : mixedRgba(departmentColor, 0.1, 0.7),
                                        }}
                                        onMouseEnter={
                                          hasVersionActivity
                                            ? (event) => {
                                                event.stopPropagation();
                                                setHoverCard({
                                                  x: event.clientX,
                                                  y: event.clientY,
                                                  kind: "version",
                                                  artistName: artist.name,
                                                  department: artist.department,
                                                  date: day,
                                                  versions: dayVersions,
                                                });
                                              }
                                            : undefined
                                        }
                                        onMouseMove={
                                          hasVersionActivity
                                            ? (event) => {
                                                event.stopPropagation();
                                                setHoverCard((current) =>
                                                  current?.kind === "version" &&
                                                  current.artistName === artist.name &&
                                                  current.date === day
                                                    ? { ...current, x: event.clientX, y: event.clientY }
                                                    : current
                                                );
                                              }
                                            : undefined
                                        }
                                        onMouseLeave={
                                          hasVersionActivity
                                            ? () =>
                                                setHoverCard((current) =>
                                                  current?.kind === "version" &&
                                                  current.artistName === artist.name &&
                                                  current.date === day
                                                    ? null
                                                    : current
                                                )
                                            : undefined
                                        }
                                      />
                                    );
                                  })}
                                </div>
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-between gap-2 px-3">
                                  <span className="truncate text-xs text-slate-800 dark:text-slate-100">
                                    <span className="font-semibold">{bookingDateLabel}</span>
                                    <span className="font-normal">{` | ${bookingDaysLabel}`}</span>
                                  </span>
                                  <span className="shrink-0 text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                                    {Math.round(Number(booking.percentageAllocation || 100))}%
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {visibleDays.map((day, index) => {
                            const dayVersions = artist.allVersionsByDay.get(day) || [];
                            if (!dayVersions.length) return null;
                            const classification = classifyVersionsForDay(dayVersions, projectId, fptProjectId);
                            if (!classification) return null;
                            const markerHeight = VERSION_INDICATOR_HEIGHT;
                            const markerWidth = markerHeight * 2;
                            return (
                              <div
                                key={`${artist.id}-${day}-all-versions`}
                                className="absolute rounded-[4px]"
                                style={{
                                  left: index * dayColumnWidth + dayColumnWidth / 2 - markerWidth / 2,
                                  top: versionIndicatorTop,
                                  width: markerWidth,
                                  height: markerHeight,
                                  backgroundColor: getVersionIndicatorColor(classification, departmentColor),
                                }}
                                onMouseEnter={(event) => {
                                  setHoverCard({
                                    x: event.clientX,
                                    y: event.clientY,
                                    kind: "version",
                                    artistName: artist.name,
                                    department: artist.department,
                                    date: day,
                                    versions: dayVersions,
                                  });
                                }}
                                onMouseMove={(event) => {
                                  setHoverCard((current) =>
                                    current?.kind === "version" &&
                                    current.artistName === artist.name &&
                                    current.date === day
                                      ? { ...current, x: event.clientX, y: event.clientY }
                                      : current
                                  );
                                }}
                                onMouseLeave={() =>
                                  setHoverCard((current) =>
                                    current?.kind === "version" &&
                                    current.artistName === artist.name &&
                                    current.date === day
                                      ? null
                                      : current
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {hoverCard && (
        <div
          className="app-card pointer-events-none fixed z-[100] w-72 border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:shadow-2xl"
          style={(() => {
            const cardWidth = 288;
            const gap = 18;
            const viewportPadding = 16;
            const preferredRight = hoverCard.x + gap;
            const wouldOverflowRight = preferredRight + cardWidth > window.innerWidth - viewportPadding;
            const left = wouldOverflowRight
              ? Math.max(viewportPadding, hoverCard.x - cardWidth - gap)
              : Math.min(preferredRight, window.innerWidth - cardWidth - viewportPadding);
            return {
              left,
              top: Math.max(16, hoverCard.y - 24),
            };
          })()}
        >
          {hoverCard.kind === "booking" ? (
            <>
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{hoverCard.artistName}</div>
              <div className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{hoverCard.department}</div>
              <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
                <div className="flex items-center justify-between">
                  <span>Booking</span>
                  <span className="font-medium">
                    {formatDateForBookingBar(hoverCard.booking.startDate)} to {formatDateForBookingBar(hoverCard.booking.endDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Working days</span>
                  <span className="font-medium">{hoverCard.bookingWorkingDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Days completed</span>
                  <span className="font-medium">{hoverCard.elapsedWorkingDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Days left</span>
                  <span className="font-medium">{hoverCard.remainingWorkingDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Allocation</span>
                  <span className="font-medium">{Math.round(Number(hoverCard.booking.percentageAllocation || 100))}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Booked labour cost</span>
                  <span className="font-medium">{formatCurrency(Number(hoverCard.booking.bookedLabourCost || 0), "GBP")}</span>
                </div>
              </div>
              <div className="app-control mt-3 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {hoverCard.completedPercent}% of this booking window has elapsed.
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm text-slate-700 dark:text-slate-300">
                <span>Publish date</span>
                <span className="font-medium">{formatDateForBookingBar(hoverCard.date)}</span>
              </div>
              <div className="mt-3 space-y-3">
                {hoverCard.versions.map((version) => (
                  <div key={version.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/80">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {version.versionName || version.versionCode || `Version ${version.fptVersionId}`}
                    </div>
                    <div className="mt-1 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                      <div><span className="font-medium text-slate-500 dark:text-slate-400">Linked:</span> {formatVersionEntityLabel(version)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
